import type { FirebaseApp } from 'firebase/app';
import { collection, onSnapshot, query, where, type Firestore, type Query } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions, httpsCallable, type Functions } from 'firebase/functions';
import type { EmulatorEndpoint } from '@taki/runtime-config';
import { FOURNISSEURS_CONNEXION, type Connexion, type FournisseurConnexion } from '@taki/data-model';
import { fusionnerParId, lireDocumentFlux, messageDErreurFlux, parModificationRecente, FLUX_FUNCTIONS_REGION, type FluxError } from './flux.js';

/**
 * LES CONNEXIONS DU SOCLE, COTE CLIENT : lire et demander
 * (`docs/socle/connexions.md`).
 *
 * Le client LIT `orgs/{orgId}/connexions` en temps reel et n'y ECRIT JAMAIS :
 * creer, modifier (nom, niveau d'acces, secret) et supprimer passent par UNE
 * callable a operations, `connexionsApi`, qui valide la saisie par le registre
 * du socle (`packages/data-model/src/connexions.ts`) et verifie les droits.
 *
 * DEUX REQUETES, ET C'EST LES RULES QUI L'IMPOSENT, comme `sdk.flux` : une
 * connexion se lit si elle est a l'org (`portee == 'org'`) OU si on en est
 * l'auteur. Deux requetes permises, fusionnees par id, triees cote client.
 *
 * LE SECRET NE DESCEND JAMAIS : le document porte un `apercu` (« ••••abcd ») ;
 * le secret vit dans `connexionsSecrets`, que les rules ferment a tout client.
 * Un secret part UNE fois, dans `appeler('creer' | 'modifier', ...)`.
 */

/** `CALLABLE_CONNEXIONS` de `functions/src/connexions/contrat.ts`. */
export const CONNEXIONS_CALLABLE = 'connexionsApi';
/** `COLLECTION_CONNEXIONS` de `functions/src/connexions/contrat.ts`. */
export const CONNEXIONS_COLLECTION = 'connexions';

/**
 * Le contrat de `sdk.connexions`. Nomme `Client`, pas `Repository` : un
 * `xxxRepository` de `TakiSdk` se lit comme une FAMILLE de fiches
 * (`scripts/check-app-entities.mjs`), et les connexions n'en sont pas une.
 */
export interface ConnexionsClient {
  /** Les connexions visibles (de l'org, et les siennes), les plus recentes d'abord. Sans compte : liste vide. */
  observer(cb: (connexions: Connexion[]) => void, erreur?: (e: unknown) => void): () => void;
  /** Le registre des fournisseurs : requetes HTTP, comptes de Lia (Google, Outlook), connecteurs de Flux (Slack, Stripe...). */
  fournisseurs(): readonly FournisseurConnexion[];
  /**
   * Une operation de `connexionsApi` (`creer`, `modifier`, `supprimer`, `fournisseurs` ; OAuth : `consentir`
   * rend l'adresse du fournisseur, `page: 'flux'` y revient dans Flux ; `choix` les agendas ou dossiers du
   * compte). Leve une erreur au message court.
   */
  appeler<T = unknown>(op: 'creer' | 'modifier' | 'supprimer' | 'fournisseurs' | 'consentir' | 'choix', donnees?: Record<string, unknown>): Promise<T>;
}

export type ConnexionsError = FluxError;

const emulatorBoundFunctions = new WeakSet<Functions>();

/**
 * @param getActorId - uid de l'utilisateur connecte (`'system'` sans compte : rien n'est lu).
 * @param emulator - `config.emulators?.functions` en dev local, sinon omis.
 */
export function createConnexionsClient(
  db: Firestore,
  getActorId: () => string,
  orgId: string,
  app: FirebaseApp,
  emulator?: EmulatorEndpoint,
): ConnexionsClient {
  let callable: ((donnees: Record<string, unknown>) => Promise<{ data: unknown }>) | null = null;
  function appelable(): (donnees: Record<string, unknown>) => Promise<{ data: unknown }> {
    if (callable !== null) return callable;
    const functions = getFunctions(app, FLUX_FUNCTIONS_REGION);
    if (emulator && !emulatorBoundFunctions.has(functions)) {
      connectFunctionsEmulator(functions, emulator.host, emulator.port);
      emulatorBoundFunctions.add(functions);
    }
    callable = httpsCallable<Record<string, unknown>, unknown>(functions, CONNEXIONS_CALLABLE);
    return callable;
  }

  return {
    observer(cb, erreur) {
      const uid = getActorId();
      if (uid === '' || uid === 'system') {
        cb([]);
        return () => undefined;
      }
      const col = collection(db, 'orgs', orgId, CONNEXIONS_COLLECTION);
      const requetes: Query[] = [query(col, where('portee', '==', 'org')), query(col, where('auteurUid', '==', uid))];
      const resultats: (Connexion[] | null)[] = requetes.map(() => null);
      const arrets = requetes.map((q, i) =>
        onSnapshot(
          q,
          (snapshot) => {
            resultats[i] = snapshot.docs.map((d) => lireDocumentFlux<Connexion>(d.id, d.data()));
            if (resultats.every((r) => r !== null)) cb(fusionnerParId(resultats, parModificationRecente));
          },
          (e) => erreur?.(e),
        ),
      );
      return () => arrets.forEach((arreter) => arreter());
    },

    fournisseurs() {
      return FOURNISSEURS_CONNEXION;
    },

    async appeler<T = unknown>(op: string, donnees: Record<string, unknown> = {}): Promise<T> {
      try {
        const { data } = await appelable()({ ...donnees, op });
        return data as T;
      } catch (e) {
        const lue = messageDErreurFlux(e);
        if (lue.code === 'unavailable' || lue.code === 'internal') lue.message = lue.message.replace('Service des automatisations', 'Service des connexions');
        throw lue;
      }
    },
  };
}
